/**
 * Returns the storage key for a specific user.
 * If userId is provided, returns `${baseKey}_${userId}`.
 * If userId is not provided (guest), returns `baseKey`.
 */
export function getUserStorageKey(baseKey: string, userId?: string | null): string {
  if (!userId) {
    return baseKey;
  }
  return `${baseKey}_${userId}`;
}

/**
 * Migrates data from the legacy (un-suffixed) key to the user-specific key
 * ONLY IF:
 * 1. The user is logged in (userId exists).
 * 2. The legacy key exists and has data.
 * 3. The new user-specific key does NOT exist.
 * 
 * This ensures that when an existing user updates the app, their data 
 * (which sits in the base key) is moved to their new user key.
 */
export function migrateLegacyData(baseKey: string, userId?: string | null): boolean {
  if (!userId) return false;

  try {
    const legacyData = localStorage.getItem(baseKey);
    const userKey = getUserStorageKey(baseKey, userId);
    const userData = localStorage.getItem(userKey);

    // Only migrate if we have legacy data AND new data slot is empty
    if (legacyData && !userData) {
      console.log(`[Storage] Migrating legacy data for ${baseKey} to ${userKey}`);
      localStorage.setItem(userKey, legacyData);
      
      // Optional: Remove legacy data to clean up?
      // For safety, we remove it so that subsequent logins by OTHER users don't inherit it.
      // This solves the "account bleed" issue.
      localStorage.removeItem(baseKey);
      return true;
    }
  } catch (e) {
    console.error(`[Storage] Failed to migrate legacy data for ${baseKey}:`, e);
  }
  return false;
}

/**
 * Helper to migrate common keys
 */
export function migrateAllUserData(userId?: string | null) {
  if (!userId) return;
  
  const commonKeys = [
    't2s_conversations',
    't2s_daily_summaries',
    't2s_emotion_records',
    't2s_persona_style',
    't2s_voice_gender',
    't2s_celebration_shown_date',
    'onboarding-data',
    'onboarding-completed',
    'app-settings', 
  ];

  let migratedCount = 0;
  commonKeys.forEach(key => {
    if (migrateLegacyData(key, userId)) {
      migratedCount++;
    }
  });
  
  if (migratedCount > 0) {
    console.log(`[Storage] Successfully migrated ${migratedCount} keys for user ${userId}`);
  }
}
